import type { ProblemDetails } from '@/types/api'

/**
 * Signetix API client — a thin `fetch` wrapper for Sanctum SPA cookie auth.
 *
 *  - same-origin requests (Vite proxies /api and /sanctum in dev)
 *  - `GET /sanctum/csrf-cookie` lazily before the first mutating request
 *  - `X-XSRF-TOKEN` taken from the `XSRF-TOKEN` cookie (URL-decoded)
 *  - RFC 9457 problem+json errors surfaced as a typed `ApiError`
 *  - global hooks for 401 (→ /login) and `no-tenant` (→ onboarding)
 */

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
const CSRF_URL = '/sanctum/csrf-cookie'

export type ProblemSlug =
  | 'validation'
  | 'unauthenticated'
  | 'forbidden'
  | 'not-found'
  | 'quota-exceeded'
  | 'feature-unavailable'
  | 'no-tenant'
  | 'two-factor-required'
  | 'rate-limited'
  | 'tenant-suspended'
  | 'csrf-mismatch'
  | 'conflict'
  | 'server-error'
  | 'network-error'
  | (string & {})

const STATUS_SLUGS: Record<number, ProblemSlug> = {
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
  419: 'csrf-mismatch',
  422: 'validation',
  423: 'tenant-suspended',
  429: 'rate-limited',
}

/** Extracts the trailing slug from a problem `type` URI (`https://signetix.io/problems/quota-exceeded` → `quota-exceeded`). */
export function problemSlug(type: string | undefined | null, status: number): ProblemSlug {
  if (type && type !== 'about:blank') {
    const trimmed = type.replace(/[/#?]+$/, '')
    const slug = trimmed.slice(trimmed.lastIndexOf('/') + 1)
    if (slug) return slug
  }
  if (STATUS_SLUGS[status]) return STATUS_SLUGS[status]
  if (status === 0) return 'network-error'
  return status >= 500 ? 'server-error' : 'unknown'
}

export class ApiError extends Error {
  readonly status: number
  readonly type: ProblemSlug
  readonly title: string
  readonly detail: string
  readonly errors: Record<string, string[]>
  readonly problem: ProblemDetails

  constructor(status: number, problem: ProblemDetails = {}) {
    const detail =
      problem.detail ??
      (typeof problem.message === 'string' ? problem.message : undefined) ??
      problem.title ??
      defaultMessage(status)
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.type = problemSlug(problem.type, status)
    this.title = problem.title ?? defaultTitle(this.type)
    this.detail = detail
    this.errors = normalizeErrors(problem.errors)
  }

  is(slug: ProblemSlug): boolean {
    return this.type === slug
  }

  /** First message for a field, if any. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0]
  }

  get isValidation() {
    return this.status === 422 || this.type === 'validation'
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function isProblem(error: unknown, slug: ProblemSlug): error is ApiError {
  return error instanceof ApiError && error.type === slug
}

function normalizeErrors(raw: unknown): Record<string, string[]> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value)) out[key] = value.map(String)
    else if (typeof value === 'string') out[key] = [value]
  }
  return out
}

function defaultMessage(status: number): string {
  if (status === 0) return 'Unable to reach the server. Check your connection and try again.'
  if (status >= 500) return 'Something went wrong on our side. Please try again.'
  return 'The request could not be completed.'
}

function defaultTitle(slug: ProblemSlug): string {
  switch (slug) {
    case 'validation':
      return 'Validation failed'
    case 'unauthenticated':
      return 'Not signed in'
    case 'forbidden':
      return 'Not allowed'
    case 'not-found':
      return 'Not found'
    case 'quota-exceeded':
      return 'Plan limit reached'
    case 'feature-unavailable':
      return 'Not included in your plan'
    case 'rate-limited':
      return 'Too many attempts'
    case 'tenant-suspended':
      return 'Organization suspended'
    default:
      return 'Request failed'
  }
}

/* ------------------------------------------------------------- global hooks */

export interface ApiEventHandlers {
  onUnauthenticated?: (error: ApiError) => void
  onNoTenant?: (error: ApiError) => void
}

let handlers: ApiEventHandlers = {}

export function setApiEventHandlers(next: ApiEventHandlers) {
  handlers = next
}

/* ------------------------------------------------------------------ cookies */

export function readCookie(name: string, source: string = typeof document !== 'undefined' ? document.cookie : ''): string | null {
  for (const part of source.split(';')) {
    const [rawKey, ...rest] = part.trim().split('=')
    if (rawKey === name) {
      const value = rest.join('=')
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }
  }
  return null
}

let csrfPromise: Promise<void> | null = null

export function ensureCsrfCookie(force = false): Promise<void> {
  if (!force && readCookie('XSRF-TOKEN')) return Promise.resolve()
  if (!csrfPromise || force) {
    csrfPromise = fetch(CSRF_URL, {
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(() => undefined)
      .finally(() => {
        csrfPromise = null
      })
  }
  return csrfPromise
}

/* ------------------------------------------------------------------ request */

type QueryScalar = string | number | boolean | null | undefined
/** Array values repeat the key (`severity[]=a&severity[]=b` — name the key with `[]` for PHP arrays). */
export type Query = Record<string, QueryScalar | readonly (string | number)[]>

export interface RequestOptions {
  query?: Query
  body?: unknown
  signal?: AbortSignal
  /** Don't fire the global 401 / no-tenant hooks (auth bootstrap, public pages). */
  silent?: boolean
  headers?: Record<string, string>
  /** Let the request outlive the page (lock release on unload). */
  keepalive?: boolean
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function buildUrl(path: string, query?: Query): string {
  const base = path.startsWith('http') || path.startsWith('/sanctum') ? path : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
      continue
    }
    if (value === undefined || value === null || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function send<T>(method: string, path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const mutating = MUTATING.has(method)
  if (mutating) await ensureCsrfCookie()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers,
  }
  const xsrf = readCookie('XSRF-TOKEN')
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf

  let body: BodyInit | undefined
  if (options.body instanceof FormData) {
    body = options.body
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: options.signal,
      keepalive: options.keepalive,
    })
  } catch (cause) {
    if ((cause as Error)?.name === 'AbortError') throw cause
    throw new ApiError(0, { detail: defaultMessage(0) })
  }

  const payload = await parseBody(response)

  if (response.ok) return payload as T

  // Stale CSRF token (session rotated): refresh once and replay.
  if (response.status === 419 && mutating && !retried) {
    await ensureCsrfCookie(true)
    return send<T>(method, path, options, true)
  }

  const problem: ProblemDetails =
    payload && typeof payload === 'object' ? (payload as ProblemDetails) : { detail: typeof payload === 'string' && payload.length < 300 ? payload : undefined }
  const error = new ApiError(response.status, problem)

  if (!options.silent) {
    if (error.status === 401 || error.type === 'unauthenticated') handlers.onUnauthenticated?.(error)
    else if (error.type === 'no-tenant') handlers.onNoTenant?.(error)
  }

  throw error
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => send<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => send<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => send<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => send<T>('PATCH', path, { ...options, body }),
  delete: <T = void>(path: string, body?: unknown, options?: RequestOptions) => send<T>('DELETE', path, { ...options, body }),
}

/** Unwraps `{data: T}`. */
export async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  return (await promise).data
}

/** Human-friendly message for toasts. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ApiError) return error.detail || error.title
  if (error instanceof Error && error.message) return error.message
  return fallback
}
